import React, { useMemo } from 'react';

import {
	Box,
	Button,
	Stack,
	type SxProps,
	type Theme,
	useMediaQuery,
} from '@mui/material';

import RouteLink from '@ivy/components/atoms/RouteLink';
import { useCrisp } from '@ivy/components/providers/CrispProvider';
import { IVY_LOGO_WHITE } from '@ivy/constants/brand';
import { useCurrentAccount } from '@ivy/gql/hooks';
import { getFooterMenuItems } from '@ivy/lib/helpers/menu';
import { combineSx } from '@ivy/lib/styling/sx';

export interface FooterProps {
	sx?: SxProps<Theme>;
}

const Footer = ({ sx }: FooterProps) => {
	const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const crisp = useCrisp();
	const currAcc = useCurrentAccount();

	const footerMenuItems = useMemo(
		() => getFooterMenuItems({ crisp }, currAcc),
		[crisp, currAcc],
	);

	return (
		<Box
			component='footer'
			sx={combineSx(
				{
					// Full bleed
					width: '100vw',
					position: 'relative',
					left: '50%',
					right: '50%',
					ml: '-50vw',
					mr: '-50vw',
				},
				sx,
			)}
		>
			<Box
				sx={{
					p: 5,
					borderRadius: (theme) => `${theme.shape.borderRadius}px`,
					backgroundImage: 'linear-gradient(180deg,#1fc173 0%,#0090b9 100%)',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					flexDirection: {
						xs: 'column',
						md: 'row',
					},
				}}
			>
				<Box
					component='img'
					src={IVY_LOGO_WHITE}
					sx={{
						height: '100px',
					}}
				/>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: {
							xs: 'center',
							md: 'initial',
						},
					}}
				>
					<Stack
						direction={isSm ? 'column' : 'row'}
						flexWrap='wrap'
						justifyContent={{
							xs: 'center',
							md: 'flex-end',
						}}
						alignItems='center'
						spacing={isSm ? 0 : 1}
						mt={{
							xs: 2,
							md: 0,
						}}
					>
						{footerMenuItems.map((item, idx) => {
							return (
								<Button
									{...(item.route && {
										component: RouteLink,
										to: item.route,
										openInNewTab: item.openInNewTab,
									})}
									onClick={item.onClick}
									color='inherit'
									key={`${item.label}-${idx}`}
									sx={{
										color: 'primary.contrastText',
										whiteSpace: 'nowrap',
									}}
									size='large'
								>
									{item.shortLabel || item.label}
								</Button>
							);
						})}
					</Stack>
				</Box>
			</Box>
		</Box>
	);
};

export default Footer;
